import React, { useState, useEffect } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';

import { usePrevious } from '@hooks/usePrevious';
import getRoleGroupByRole from '@utils/dashboard/getRoleGroupbyRole';
import NavBarContainer from '../common/NavBarContainer';
import { configs } from '@constants/configs/configs';
import SideNav from '@components/individualPortal/navigation/SideNavigation';
import DelegationView from '@components/delegation/DelegationView';
import EnrollerAnnouncements from '@components/common/EnrollerAnnouncements';
import {
    fields,
    labelNames,
    filtersByRole,
    tableHeadersByRole,
    filterInputsMaxLength,
    DELEGATION_REASONS,
    DELEGATION_TYPE
} from '@constants/delegation/delegationConfig';
import { USER_GROUPS, ALL_USER_ROLES } from '@constants/roles/userRoleTypes';
import { useExchangeConditions } from '@hooks/useExchangeConditions';
import { brokerNavLinks } from '../individualPortal/navigation/configs/brokerSideNavConfigs';
import { assisterNavLinks } from '../individualPortal/navigation/configs/assisterSideNavConfigs';
import {
    fetchDelegationData,
    initDelegationFilters,
    setFilterValue,
    resetFilterValues,
    fetchNavBarData,
    fetchLeftNavParams
} from '@actions/delegation/delegationActionCreators';

import { useIntl } from 'react-intl';
import {
    IDelegationHistoryItem,
    IDelegationContainerProps,
    IRootState
} from '@components/delegation/delegationTypes';

const DelegationContainer: React.FC<IDelegationContainerProps> = (props) => {
    const { endpoints } = configs;
    const {
        actions,
        spinner,
        environment: {
            stateCode,
            isState,
            impersonation,
            impersonationDetails,  
            user: {
                activeModuleName
            },
            language,
            ticketEnabled,
            designationHistoryEnabled,
            manageAgentNPN,
            flags: {
                enrollmentDetailsEnabled
            }
        },
        delegation: {
            history,
            filters,
            pagination,
            leftNavParams
        }
    } = props;

    const intlApi = useIntl();
    const { isStateWithNavAndBrokerLabels } = useExchangeConditions();
    const role= activeModuleName.toUpperCase();
    const roleGroup = !impersonation ? getRoleGroupByRole(role) : getRoleGroupByRole(impersonationDetails.role);

    const createFilterObj = (arr: string[]): Record<string, string> => 
        arr.reduce((o, key) => ({ ...o, [key]: '' }), {} as Record<string, string>);
    
    const isManageAgentNPN = manageAgentNPN === true;

    const resolvedLabelNames = {
        ...labelNames,
        [fields.AGENT_LICENCE_NUMBER]: isManageAgentNPN
            ? 'common.agent_npn'
            : labelNames[fields.AGENT_LICENCE_NUMBER],
    };

    // License number is capped at 11 digits; NPN has no max length.
    const resolvedFilterInputsMaxLength = {
        ...filterInputsMaxLength,
        [fields.AGENT_LICENCE_NUMBER]: isManageAgentNPN
            ? null
            : filterInputsMaxLength[fields.AGENT_LICENCE_NUMBER],
    };

    const createTableHeaders = (arr: string[]) => 
        arr.map(item => ({ 'id': item, 'text': intlApi.formatMessage({ id: resolvedLabelNames[item] }) }));

    // Delegation pages for AGENCY_GROUP and CSR_GROUP should be rendered full width.
    const containerWidth = (roleGroup === USER_GROUPS.AGENCY_GROUP || roleGroup === USER_GROUPS.CSR_GROUP) ?
        'grid-col-12' : 'grid-col-9';

    const filtersData = createFilterObj(filtersByRole[roleGroup]);
    const tableHeadersData = tableHeadersByRole[roleGroup];

    const [householdCaseIdFlag, setHouseholdCaseIdFlag] = useState<boolean>(true);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const prevHouseholdCaseIdFlag = usePrevious(householdCaseIdFlag);

    useEffect(() => {
        if ([ALL_USER_ROLES.BROKER, ALL_USER_ROLES.ASSISTER].includes(roleGroup)) {
            actions.fetchLeftNavParams(endpoints.AGENT_LEFT_NAV_PARAMS);
        }
        if (designationHistoryEnabled) {
            actions.initDelegationFilters(filtersData);
        }
        // Only for CSR group householdCaseId should be pre populated.
        if (roleGroup === USER_GROUPS.CSR_GROUP || roleGroup === USER_GROUPS.ADMIN) {
            const householdCaseId = localStorage.getItem('householdCaseId') || '';
            actions.setFilterValue({ name: 'householdCaseId', value: householdCaseId });
        }
        setHouseholdCaseIdFlag(false);
    }, []);

    useEffect(() => {
        if (prevHouseholdCaseIdFlag !== undefined && prevHouseholdCaseIdFlag) {
            const filtersQueryParams = constructQueryParams(filters);
            const fetchStringParams = filtersQueryParams ? `&${filtersQueryParams}` : '';

            actions.fetchDelegationData(`${endpoints.DELEGATION_HISTORY.processReplacements({ pageNumber: pageNumber + 1 })}${fetchStringParams}`);
        }
    }, [filters.householdCaseId, prevHouseholdCaseIdFlag]);

    /**
     * Creates string of non empty filters values.
     * @param filters
     * @returns {string}
     */
    const constructQueryParams = (filters: Record<string, string>): string => 
        Object.keys(filters).map(key => filters[key] ? `${key}=${filters[key]}` : '').filter(Boolean).join('&');

    const getHouseholdPrimaryName = (
        condition: boolean, 
        { encryptedHouseholdId, primaryContactFirstName, primaryContactLastName }: Pick<IDelegationHistoryItem, 'encryptedHouseholdId' | 'primaryContactFirstName' | 'primaryContactLastName'>
    ): React.ReactNode => {
        return condition ? 
            <a 
                className="gi-txt__capitalize" 
                href={endpoints.VIEW_MEMBER.processReplacements({ encryptedHouseholdId: encryptedHouseholdId || '' })}
            >
                {primaryContactFirstName?.toLowerCase()} {primaryContactLastName?.toLowerCase()}
            </a> :
            <span className="gi-txt__capitalize">{primaryContactFirstName?.toLowerCase()} {primaryContactLastName?.toLowerCase()}</span>;
    };

    /**
     * Formats delegation history table data: concats firstName and lastName.
     * @param delegationHistory
     * @returns {Record<string, any>[]}
     */
    const processedTableData = (delegationHistory: IDelegationHistoryItem[]): Record<string, any>[] => {
        const result: Record<string, any>[] = [];
        delegationHistory.forEach(row => {
            const processedRow: Record<string, any> = {};
            for (const headerName of tableHeadersData) {
                switch (headerName) {
                    case fields.HOUSEHOLD_PRIMARY: {
                        let condition = roleGroup === USER_GROUPS.CSR_GROUP ? 
                            row[fields.HOUSEHOLD_CASE_ID] : 
                            !row[fields.DESIGNATION_END_DATE] && row[fields.HOUSEHOLD_CASE_ID];
                        condition = enrollmentDetailsEnabled && !!condition;
                        processedRow[headerName] = getHouseholdPrimaryName(condition, row);
                        break;
                    }
                    case fields.AGENT_CEC_NAME:
                    case fields.AGENT_NAME: {
                        const firstName = row.agentCecFirstName ? row.agentCecFirstName.toLowerCase() : '';
                        const lastName = row.agentCecLastName ? row.agentCecLastName.toLowerCase() : '';
                        processedRow[headerName] = <span className="gi-txt__capitalize">{firstName} {lastName}</span>;
                        break;
                    }
                    case fields.DESIGNATION_END_DATE: {
                        processedRow[headerName] = row[headerName] ? row[headerName] : intlApi.formatMessage({ id: 'designation.date.present' });
                        break;
                    }
                    case fields.REASON: {
                        processedRow[headerName] = !row[headerName] ? '-' : intlApi.formatMessage({ id: DELEGATION_REASONS[row[headerName]] });
                        break;
                    }
                    case fields.TYPE: {
                        if (isStateWithNavAndBrokerLabels && row[headerName] === DELEGATION_TYPE.CEC) {
                            processedRow[headerName] = DELEGATION_TYPE.NAVIGATOR;
                        } else if (isStateWithNavAndBrokerLabels && row[headerName] === DELEGATION_TYPE.AGENT) {
                            processedRow[headerName] = DELEGATION_TYPE.BROKER;
                        } else {
                            processedRow[headerName] = row[headerName];
                        }
                        break;
                    }
                    default: {
                        processedRow[headerName] = row[headerName] ? row[headerName] : '-';
                        break;
                    }
                }
            }
            result.push(processedRow);
        });
        return result;
    };

    /**
     * Filter input onChange handler. Dispatches action to save values to a redux store.
     * @param e
     */
    const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;
        actions.setFilterValue({ name, value });
    };

    /**
     * Collects values from all filters, constructs query param string and fetches new delegation data.
     */
    const onApplyHandler = (): void => {
        const params = constructQueryParams(filters);
        const queryParams = params ? `&${params}` : '';
        actions.fetchDelegationData(`${endpoints.DELEGATION_HISTORY.processReplacements({ pageNumber: 1 })}${queryParams}`);
        setPageNumber(0);
    };

    /**
     * Clears filters to initial state and updates redux store.
     */
    const onResetHandler = (): void => {
        actions.resetFilterValues(filtersData);
        setPageNumber(0);
        actions.fetchDelegationData(`${endpoints.DELEGATION_HISTORY.processReplacements({ pageNumber: 1 })}`);
    };

    /**
     * Makes a new call to fetch new set of data.
     * @param data
     */
    const onPageNumberChange = (data: { selected: number }): void => {
        const params = constructQueryParams(filters);
        const queryParams = params ? `&${params}` : '';
        setPageNumber(data.selected);
        actions.fetchDelegationData(`${endpoints.DELEGATION_HISTORY.processReplacements({ pageNumber: data.selected + 1 })}${queryParams}`);
    };

    const LeftNav: React.FC = () => {
        if (roleGroup !== ALL_USER_ROLES.BROKER && roleGroup !== ALL_USER_ROLES.ASSISTER) {
            return null;
        }

        let navLinksByRole: any = '';
        const { switchAccUrl, encryptedBrokerAssisterId, selfDelegationSwitchAccUrl } = leftNavParams;

        switch (roleGroup) {
            case ALL_USER_ROLES.BROKER:
                navLinksByRole = brokerNavLinks;
                break;
            case ALL_USER_ROLES.ASSISTER:
                navLinksByRole = assisterNavLinks;
                break;
            default:
                break;
        }

        return (
            <aside className="usa-layout-docs__sidenav desktop:grid-col-3" id="side-nav">
                {leftNavParams && Object.keys(leftNavParams).length > 0 &&
                    <SideNav
                        {...{ stateCode }}
                        navLinks={navLinksByRole}
                        userRole={role}
                        visibilityParams={{ ...leftNavParams, ticketEnabled }}
                        replacements={{ switchAccUrl, encryptedBrokerAssisterId, selfDelegationSwitchAccUrl, lang: language === 'en' ? 'en_US' : 'es_ES' }}
                    />}
            </aside>
        );
    };

    const delegationViewProps = { 
        ...{
            isState,
            roleGroup,
            filters,
            spinner, 
            pagination,
            pageNumber,
            resolvedLabelNames,
            resolvedFilterInputsMaxLength,
            onChangeHandler,
            onResetHandler,
            onApplyHandler,
            onPageNumberChange 
        },
        tableData: processedTableData(history),
        tableColumns: createTableHeaders(tableHeadersData) 
    };

    const getAllowedUserRolesForNavBar = (): string[] => {
        const allowedUserRoles = [
            USER_GROUPS.AGENCY_GROUP,
            ALL_USER_ROLES.BROKER,
            ALL_USER_ROLES.ASSISTER,
            USER_GROUPS.CSR_GROUP
        ];

        return allowedUserRoles;
    };

    return (designationHistoryEnabled ?
        roleGroup === ALL_USER_ROLES.INDIVIDUAL  ?
            // Delegation as part of Individual Portal Container(it has it's own sideNav and layout)
            <div id="delegation" className="delegation">
                <DelegationView {...delegationViewProps}/>

            </div> :

        // Delegation default as a separate page
            <div id="delegation" className="usa-layout-docs usa-section gi-main-section delegation">
                <div className="grid-container grid-widescreen gi-grid-container">
                    <div className="grid-row">
                        <NavBarContainer
                            navBarClass='grid-col-12 delegation-top-nav'
                            roleGroup={roleGroup}
                            arrayOfUserRoles={getAllowedUserRolesForNavBar()}
                            houseIcon={true}
                        />
                    </div>
                    <div className="grid-row">
                        <div className="grid-col-12">
                            <EnrollerAnnouncements />
                        </div>
                    </div>
                    <div className="grid-row grid-gap">
                        <LeftNav/>
                        <div className={`usa-layout-docs__main desktop:${containerWidth}`} id="main-content">
                            <DelegationView {...delegationViewProps}/>
                        </div>
                    </div>
                </div>
            </div> : <p> Delegation history is not enabled</p>
    );
};

const mapStateToProps = (state: IRootState) => ({
    spinner: state.common.spinner,
    environment: state.common.environment,
    delegation: state.delegation
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    actions: bindActionCreators({
        fetchDelegationData,
        initDelegationFilters,
        setFilterValue,
        resetFilterValues,
        fetchNavBarData,
        fetchLeftNavParams
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(DelegationContainer);

